import React from 'react';
import {
  CheckIcon,
  XMarkIcon,
  ShieldCheckIcon,
  UserIcon,
  EnvelopeIcon,
  DocumentTextIcon,
  CogIcon,
  ChartBarIcon,
  GlobeAltIcon,
  BuildingOfficeIcon,
  TagIcon
} from '@heroicons/react/24/outline';

const UserPrivilegeMatrix = ({ user }) => {
  if (!user || !user.privileges) {
    return (
      <div className="bg-white rounded-xl shadow-lg p-6 border border-gray-200">
        <h3 className="text-lg font-bold text-gray-900 mb-4">User Privileges</h3>
        <p className="text-gray-500">Loading privilege information...</p>
      </div>
    );
  }

  const { privileges } = user;

  const PermissionItem = ({ icon: Icon, label, hasPermission, description }) => (
    <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg hover:bg-gray-100 transition-colors">
      <div className="flex items-center space-x-3">
        <Icon className="h-5 w-5 text-gray-600" />
        <div>
          <span className="text-sm font-medium text-gray-900">{label}</span>
          {description && (
            <p className="text-xs text-gray-500 mt-1">{description}</p>
          )}
        </div>
      </div>
      <div className="flex items-center">
        {hasPermission ? (
          <div className="flex items-center text-green-700 bg-green-100 px-3 py-1 rounded-full">
            <CheckIcon className="h-4 w-4 mr-1" />
            <span className="text-xs font-bold">Enabled</span>
          </div>
        ) : (
          <div className="flex items-center text-red-700 bg-red-100 px-3 py-1 rounded-full">
            <XMarkIcon className="h-4 w-4 mr-1" />
            <span className="text-xs font-bold">Disabled</span>
          </div>
        )}
      </div>
    </div>
  );

  const permissionGroups = [
    {
      title: 'Contact Management',
      icon: UserIcon,
      permissions: [
        { key: 'canViewContacts', label: 'View Contacts', description: 'Access to contact database' },
        { key: 'canCreateContacts', label: 'Create Contacts', description: 'Add new contacts to database' },
        { key: 'canEditContacts', label: 'Edit Contacts', description: 'Modify existing contact information' },
        { key: 'canDeleteContacts', label: 'Delete Contacts', description: 'Remove contacts from database' }
      ]
    },
    {
      title: 'Email & Communication',
      icon: EnvelopeIcon,
      permissions: [
        { key: 'canComposeEmail', label: 'Compose Email', description: 'Create and send emails' },
        { key: 'sendEmailIndividually', label: 'Send Email Individually', description: 'Send emails using own configured sender credentials' },
        { key: 'canViewTemplates', label: 'View Templates', description: 'Access email templates' },
        { key: 'canCreateTemplates', label: 'Create Templates', description: 'Design new email templates' },
        { key: 'canEditTemplates', label: 'Edit Templates', description: 'Modify existing templates' },
        { key: 'canDeleteTemplates', label: 'Delete Templates', description: 'Remove email templates' }
      ]
    },
    {
      title: 'Campaign Management',
      icon: ChartBarIcon,
      permissions: [
        { key: 'canViewCampaigns', label: 'View Campaigns', description: 'Access campaign dashboard' },
        { key: 'canCreateCampaigns', label: 'Create Campaigns', description: 'Launch new email campaigns' },
        { key: 'canEditCampaigns', label: 'Edit Campaigns', description: 'Modify campaign settings' },
        { key: 'canDeleteCampaigns', label: 'Delete Campaigns', description: 'Remove campaigns' }
      ]
    },
    {
      title: 'System Administration',
      icon: CogIcon,
      permissions: [
        { key: 'canManageUsers', label: 'Manage Users', description: 'Create and edit user accounts' },
        { key: 'canViewUserActivity', label: 'View User Activity', description: 'Monitor user actions and statistics' },
        { key: 'canViewActivityLogs', label: 'View Activity Logs', description: 'Access system audit logs' },
        { key: 'canManageCategories', label: 'Manage Categories', description: 'Configure contact categories' },
        { key: 'canAccessGmailInbox', label: 'Gmail Inbox', description: 'Access Gmail integration features' },
        { key: 'canAccessMobileView', label: 'Mobile Access', description: 'Access the platform from mobile devices' }
      ]
    },
    {
      title: 'Data Export',
      icon: DocumentTextIcon,
      permissions: [
        { key: 'canExportExcel', label: 'Export to Excel', description: 'Download data as Excel spreadsheets' },
        { key: 'canExportPDF', label: 'Export to PDF', description: 'Generate PDF reports' }
      ]
    }
  ];

  return (
    <div className="bg-white">
      {/* Access Restrictions Summary */}
      <div className="px-6 py-4 bg-gray-50 border-b border-gray-200">
        <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center">
          <ShieldCheckIcon className="h-5 w-5 text-blue-600 mr-2" />
          Access Level: <span className="ml-2 text-blue-600">{privileges.accessLevel}</span>
        </h4>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="flex items-center space-x-2">
            <GlobeAltIcon className="h-4 w-4 text-gray-600" />
            <span className="text-xs text-gray-700">
              Countries: {privileges.hasCountryRestrictions ?
                `${user.countries.length} restricted` :
                'All countries'
              }
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <BuildingOfficeIcon className="h-4 w-4 text-gray-600" />
            <span className="text-xs text-gray-700">
              Institution Types: {privileges.hasInstitutionTypeRestrictions ?
                `${user.institutionTypes.length} types` :
                'All types'
              }
            </span>
          </div>
          <div className="flex items-center space-x-2">
            <TagIcon className="h-4 w-4 text-gray-600" />
            <span className="text-xs text-gray-700">
              Regions: {privileges.hasRegionRestrictions ?
                `${user.regions.length} restricted` :
                'All regions'
              }
            </span>
          </div>
        </div>
      </div>

      {/* Permission Groups */}
      <div className="px-6 py-4 space-y-6">
        {permissionGroups.map((group, groupIndex) => (
          <div key={groupIndex}>
            <div className="flex items-center space-x-2 mb-3">
              <group.icon className="h-5 w-5 text-blue-600" />
              <h4 className="text-sm font-bold text-gray-900">{group.title}</h4>
            </div>
            <div className="space-y-2">
              {group.permissions.map((permission, permIndex) => (
                <PermissionItem
                  key={permIndex}
                  icon={group.icon}
                  label={permission.label}
                  description={permission.description}
                  hasPermission={privileges[permission.key]}
                />
              ))}
            </div>
          </div>
        ))}
      </div>

      {/* Footer */}
      <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
        <div className="flex items-center justify-between text-xs text-gray-600">
          <span>Role: <span className="font-semibold capitalize">{user.role.replace(/_/g, ' ')}</span></span>
          <span>Last Updated: {new Date().toLocaleDateString()}</span>
        </div>
      </div>
    </div>
  );
};

export default UserPrivilegeMatrix;